import React, { useState, useEffect, useMemo } from "react";
import { Search, Plus, Pencil, Trash2, ChevronRight, ChevronDown, Edit2, FileText, Eye } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";
import { Package, AlertTriangle, XCircle, BarChart3, ArrowRightLeftIcon } from "lucide-react";
import AddLocationModal from "./components/AddLocation";
import AddProducts from "./components/AddProducts";
import TransferStockModal from "./components/TransferStockModal";
import RestockModal from "./components/RestockModal";
import ThemedTable from "../../components/ThemedTable";
import Loader from "../../components/Loader";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useLocation } from "react-router-dom";
import ProductRestockHistory from "./components/ProductRestockHistory";
import ProductDetailsModal from "./components/ProductDetailsModal";

const DEFAULT_UNITS = ["piece", "packet", "kg", "ltr", "gm"];

const Product = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchInput);
    }, 150);
    return () => clearTimeout(handler);
  }, [searchInput]);
  const location = useLocation();
  const highlightId = new URLSearchParams(location.search).get("highlight");
  const [activeHighlightId, setActiveHighlightId] = useState(null);

  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [imagesPreview, setImagesPreview] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [deletedImages, setDeletedImages] = useState([]);

  // 🔥 TRANSFER MODAL STATES
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferProduct, setTransferProduct] = useState(null);
  const [transferFrom, setTransferFrom] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferQty, setTransferQty] = useState("");
  const [transferBatchId, setTransferBatchId] = useState("");

  // 🔥 RESTOCK MODAL STATES
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockProductObj, setRestockProductObj] = useState(null);
  const [expiryFilter, setExpiryFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [viewProductObj, setViewProductObj] = useState(null);

  const handleViewDetails = async (p) => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/product/${p._id}`);
      setViewProductObj(res.data || p);
    } catch (err) {
      console.error("Failed to fetch product details:", err);
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getTotalStock = (warehouses = []) =>
    warehouses.reduce((sum, w) => sum + Number(w.quantity || 0), 0);

  // const getWarehouseNames = (warehouses = [], locations = []) => {
  //   return warehouses
  //     .map((w) => {
  //       const loc = locations.find((l) => l._id === w.locationId);
  //       return loc?.name;
  //     })
  //     .filter(Boolean);
  // };

  const [productData, setProductData] = useState({
    name: "",
    brand: "",
    category: "",
    quantityValue: "",
    quantityUnit: "",
    mrp: "",
    price: "",
    purchasePrice: "",
    wholesalePrice: "",
    gymPrice: "",
    taxInclude: true,
    taxRate: "",
    priceWithGst: 0,
    servings: "",
    dailyUsage: "",
    rating: 0,
    description: "",
    featured: false,
    thumbnail: null,
    images: [],
    warehouses: [{ locationId: "", quantity: 0, expiryDate: "", batchId: "" }],
  });


  const [filterStock, setFilterStock] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [filterFeatured, setFilterFeatured] = useState("");

  const totalProducts = useMemo(() => products.length, [products]);

  const lowStock = useMemo(() => {
    return products.filter((p) => {
      const total = getTotalStock(p.warehouses);
      return total > 0 && total <= 10;
    }).length;
  }, [products]);

  const outOfStock = useMemo(() => {
    return products.filter((p) => getTotalStock(p.warehouses) === 0).length;
  }, [products]);

  const mostStockProduct = useMemo(() => {
    return products.reduce((max, p) => {
      const stock = getTotalStock(p.warehouses);
      if (!max || stock > getTotalStock(max.warehouses)) {
        return p;
      }
      return max;
    }, null);
  }, [products]);



  // FETCH DATA
  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (highlightId && products.length > 0) {
      setTimeout(() => {
        const element = document.getElementById(`product-row-${highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [highlightId, products]);

  const fetchAll = async () => {
    try {
      setLoading(true); // 🔥 START LOADING

      const [cat, brand, prod, loc] = await Promise.all([
        apiClient.get("/category"),
        apiClient.get("/brands"),
        apiClient.get("/products?lite=true"),
        apiClient.get("/locations"),
      ]);

      setCategories(cat.data.data || cat.data);
      setBrands(brand.data.data || brand.data);

      const productData = Array.isArray(prod.data)
        ? prod.data
        : prod.data.data || [];

      setProducts(productData);

      setLocations(loc.data.locations || loc.data.data || loc.data);

    } catch (error) {
      console.log("Fetch error:", error);
    } finally {
      setLoading(false); // 🔥 STOP LOADING
    }
  };



  const handleAddLocationFromProduct = async () => {
    if (!newLocationName.trim() || !newAddress.trim()) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const res = await apiClient.post("/locations/create", {
        name: newLocationName,
        address: newAddress,
      });

      const newLoc = res.data.location;

      // update list
      setLocations((prev) => [...prev, newLoc]);

      // auto-select new location in product form
      setProductData((prev) => ({
        ...prev,
        location: newLoc._id,
      }));

      setShowLocationModal(false);
      setNewLocationName("");
      setNewAddress("");
      toast.success("Location added!");
    } catch (err) {
      toast.error("Failed to add location");
    }
  };
  const getInitials = (name = "") => {
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0][0]?.toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const openAdd = () => {
    setEditingId(null);

    setProductData({
      name: "",
      brand: "",
      category: "",

      quantityValue: "",
      quantityUnit: "",

      mrp: "",
      price: "",
      purchasePrice: "",
      wholesalePrice: "",
      gymPrice: "",

      taxInclude: true,
      taxRate: "",

      priceWithGst: 0,

      servings: "",
      dailyUsage: "",

      rating: 0,

      description: "",

      featured: false,

      thumbnail: null,

      images: [],

      warehouses: [
        {
          locationId: "",
          quantity: 0,
          expiryDate: "",
          batchId: "",
        },
      ],
    });

    setThumbnailPreview("");
    setImagesPreview([]);

    setShowModal(true);
  };

  const openEdit = async (origProduct) => {
    let p = origProduct;
    try {
      setLoading(true);
      const res = await apiClient.get(`/product/${origProduct._id}`);
      p = res.data || origProduct;
    } catch (err) {
      console.error("Failed to fetch product details:", err);
      toast.error("Failed to load product details");
      return;
    } finally {
      setLoading(false);
    }

    setEditingId(p._id);

    setProductData({
      name: p.name || "",

      brand: p.brand?._id || "",

      category: p.category?._id || "",

      quantityValue: p.quantityValue || "",

      quantityUnit: p.quantityUnit || "",

      mrp: p.mrp || "",

      price: p.price || "",
      purchasePrice: p.purchasePrice || "",
      wholesalePrice: p.wholesalePrice || "",
      gymPrice: p.gymPrice || "",

      taxInclude: p.taxInclude !== false,

      taxRate: p.taxRate || "",

      priceWithGst: p.priceWithGst || 0,

      servings: p.servings || "",

      dailyUsage: p.dailyUsage || "",

      rating: p.rating || 0,

      description: p.description || "",

      featured: p.featured || false,

      thumbnail: null,

      images: [],

      warehouses:
        Array.isArray(p.warehouses) &&
        p.warehouses.length > 0
          ? p.warehouses.map((w) => ({
              locationId: w.location?._id || w.locationId || "",
              quantity: w.quantity || 0,
              expiryDate: w.expiryDate ? w.expiryDate.split("T")[0] : "",
              batchId: w.batchId || "",
            }))
          : [
              {
                locationId: "",
                quantity: 0,
                expiryDate: "",
                batchId: "",
              },
            ],
    });

    setDeletedImages([]);

    setThumbnailPreview(
      p.thumbnail
        ? `${process.env.REACT_APP_IMAGE_URL}/${p.thumbnail}`
        : ""
    );

    setImagesPreview(
      Array.isArray(p.images)
        ? p.images.map(
            (img) =>
              `${process.env.REACT_APP_IMAGE_URL}/${img}`
          )
        : []
    );

    setShowModal(true);
  };

  const deleteProduct = async (id) => {
    setDeleting(true);
    try {
      await apiClient.delete(`/product/${id}`);
      setProducts(products.filter((p) => p._id !== id));
      setDeleteProductObj(null);
    } catch (err) {
      toast.error("Failed to delete product");
    } finally {
      setDeleting(false);
    }
  };

  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

  const handleThumbnail = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG, GIF, and WEBP images are allowed");
      e.target.value = "";
      return;
    }

    setProductData({ ...productData, thumbnail: file });
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const MAX_IMAGES = 4;

  const handleImages = (e) => {
    const files = Array.from(e.target.files);

    const invalidFiles = files.filter(file => !ALLOWED_IMAGE_TYPES.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error("Only JPG, JPEG, PNG, GIF, and WEBP images are allowed");
      e.target.value = "";
      return;
    }

    // current images count
    const currentCount = productData.images.length;

    // remaining slots
    const remaining = MAX_IMAGES - currentCount;

    if (remaining <= 0) {
      toast.error("You can upload only 4 images");
      return;
    }

    const filesToAdd = files.slice(0, remaining);

    setProductData((prev) => ({
      ...prev,
      images: [...prev.images, ...filesToAdd],
    }));

    setImagesPreview((prev) => [
      ...prev,
      ...filesToAdd.map((file) => URL.createObjectURL(file)),
    ]);

    // reset input so same file can be reselected
    e.target.value = "";
  };

  const removeImage = (index) => {
    const img = imagesPreview[index];

    // if image is from backend (URL), mark it for deletion
    if (img.startsWith(process.env.REACT_APP_IMAGE_URL)) {
      const filename = img.split("/").pop();
      setDeletedImages((prev) => [...prev, filename]);
    }

    setImagesPreview((prev) => prev.filter((_, i) => i !== index));

    setProductData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const saveProduct = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      // TEXT FIELDS
      formData.append("name", productData.name);
      formData.append("brand", productData.brand);
      formData.append("category", productData.category);

      formData.append(
        "quantityValue",
        productData.quantityValue
      );

      formData.append(
        "quantityUnit",
        productData.quantityUnit
      );

      formData.append("mrp", productData.mrp);

      formData.append("price", productData.price);
      formData.append("purchasePrice", productData.purchasePrice || 0);
      formData.append("wholesalePrice", productData.wholesalePrice || 0);
      formData.append("gymPrice", productData.gymPrice || 0);

      formData.append(
        "taxInclude",
        productData.taxInclude
      );

      formData.append(
        "taxRate",
        productData.taxRate || 0
      );

      // Expiry and Batch are stored inside the warehouses array

      formData.append(
        "servings",
        productData.servings || ""
      );

      formData.append(
        "dailyUsage",
        productData.dailyUsage || ""
      );

      formData.append(
        "rating",
        productData.rating || 0
      );

      formData.append(
        "description",
        productData.description || ""
      );

      formData.append(
        "featured",
        productData.featured
      );

      // WAREHOUSES
      formData.append(
        "warehouses",
        JSON.stringify(productData.warehouses)
      );

      // THUMBNAIL
      if (productData.thumbnail instanceof File) {
        formData.append(
          "thumbnail",
          productData.thumbnail
        );
      }

      // IMAGES
      if (Array.isArray(productData.images)) {
        productData.images.forEach((img) => {
          if (img instanceof File) {
            formData.append("images", img);
          }
        });
      }

      // DELETE IMAGES
      if (editingId && deletedImages.length > 0) {
        formData.append(
          "deletedImages",
          JSON.stringify(deletedImages)
        );
      }

      // API CALL
      let savedProductId = editingId;
      if (editingId) {
        const res = await apiClient.put(
          `/product/${editingId}`,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        const updated = res.data?.product || res.data;
        if (updated && updated._id) {
          setProducts((prev) =>
            prev.map((p) => (p._id === editingId ? { ...p, ...updated } : p))
          );
        }

        toast.success("Product updated successfully");

      } else {

        const res = await apiClient.post(
          "/product/add",
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        const newProd = res.data?.product || res.data;
        if (newProd && newProd._id) {
          savedProductId = newProd._id;
          setProducts((prev) => [newProd, ...prev]);
        }

        toast.success("Product added successfully");
      }

      setShowModal(false);

      if (savedProductId) {
        setActiveHighlightId(savedProductId);
        setTimeout(() => {
          const el = document.getElementById(`product-row-${savedProductId}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 150);
        setTimeout(() => setActiveHighlightId(null), 3000);
      }

    } catch (error) {

      console.log(error);

      toast.error(
        error.response?.data?.message ||
        "Error saving product"
      );
    }
  };



  const handleTransferStock = async ({ fromLocationId, toLocationId, quantity, batchId, expiryDate }) => {
    if (loading) return; // ✅ prevent double click

    if (!fromLocationId || !toLocationId || !quantity) {
      toast.error("All fields are required");
      return;
    }

    if (fromLocationId === toLocationId) {
      toast.error("Source and destination cannot be same");
      return;
    }

    const prodId = transferProduct?._id;

    try {
      const res = await apiClient.put(
        `/product/transfer/${prodId}`,
        {
          fromLocationId,
          toLocationId,
          quantity: Number(quantity),
          batchId,
          expiryDate,
        }
      );

      const updated = res.data?.product || res.data;
      if (updated && updated._id) {
        setProducts((prev) =>
          prev.map((p) => (p._id === prodId ? { ...p, ...updated } : p))
        );
      }

      toast.success("Stock transferred successfully");
      setShowTransferModal(false);
      setTransferProduct(null);

      if (prodId) {
        setActiveHighlightId(prodId);
        setTimeout(() => {
          const el = document.getElementById(`product-row-${prodId}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 150);
        setTimeout(() => setActiveHighlightId(null), 3000);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Transfer failed");
    }
  };

  const handleRestockStock = async ({ restockEntries, batchId, expiryDate, overwrite }) => {
    if (loading) return;
    const prodId = restockProductObj?._id;

    try {
      const formData = new FormData();
      
      const currentWarehouses = overwrite ? [] : restockProductObj.warehouses.map(w => ({
        locationId: w.location?._id || w.locationId,
        quantity: w.quantity || 0,
        expiryDate: w.expiryDate ? w.expiryDate.split("T")[0] : null,
        batchId: w.batchId || "",
      }));

      // Find the warehouse to add stock to, or push it
      restockEntries.forEach(({ warehouseId, quantity }) => {
        const qty = Number(quantity);
        if (qty > 0) {
          const targetW = currentWarehouses.find(
            w => w.locationId === warehouseId &&
                 (expiryDate ? w.expiryDate === expiryDate : !w.expiryDate)
          );
          if (targetW) {
            targetW.quantity += qty;
          } else {
            currentWarehouses.push({
              locationId: warehouseId,
              quantity: qty,
              expiryDate: expiryDate || null,
              batchId: batchId || "",
            });
          }
        }
      });

      formData.append("warehouses", JSON.stringify(currentWarehouses));

      const res = await apiClient.put(`/product/${prodId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updated = res.data?.product || res.data;
      if (updated && updated._id) {
        setProducts((prev) =>
          prev.map((p) => (p._id === prodId ? { ...p, ...updated } : p))
        );
      }

      toast.success("Product restocked successfully!");
      setShowRestockModal(false);
      setRestockProductObj(null);

      if (prodId) {
        setActiveHighlightId(prodId);
        setTimeout(() => {
          const el = document.getElementById(`product-row-${prodId}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 150);
        setTimeout(() => setActiveHighlightId(null), 3000);
      }
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || "Restock failed");
    }
  };

  const [deleteProductObj, setDeleteProductObj] = useState(null);
  const [deleting, setDeleting] = useState(false);

  return (
    <div className="text-sm">
      {/* 🚀 Enable scrollbar on the main page container ONLY when viewing Products */}
      <style>{`
        main {
          scrollbar-width: thin !important;
          scrollbar-color: #94a3b8 #f1f5f9 !important;
        }
        main::-webkit-scrollbar {
          display: block !important;
          width: 10px !important;
        }
        main::-webkit-scrollbar-track {
          background: #f1f5f9 !important;
        }
        main::-webkit-scrollbar-thumb {
          background: #94a3b8 !important;
          border-radius: 9999px !important;
        }
        main::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }
      `}</style>

      {/* HEADER */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Product Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage products, stock and locations</p>
        </div>
        <button
          onClick={openAdd}
          className="px-4 py-2 me-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
        >
          Add Product
        </button>
      </div>

      {/* ================= STATS (CONTINUOUS ROW) ================= */}
      <div className="mb-0 mt-0 bg-white overflow-hidden">
        <div className="flex flex-col sm:flex-row">


          {/* TOTAL PRODUCTS */}
          <div className="flex-1 border-b sm:border-b-0 sm:border-r border-slate-200 p-4 flex flex-col items-center justify-center text-center">
            <p className="text-xs sm:text-sm text-slate-600">Total Products</p>
            <p className="text-2xl font-bold text-slate-900">{totalProducts}</p>
          </div>

          {/* LOW STOCK */}
          <div className="flex-1 border-b sm:border-b-0 sm:border-r border-slate-200 p-4 flex flex-col items-center justify-center text-center">
            <p className="text-xs sm:text-sm text-slate-600">Low Stock</p>
            <p className="text-2xl font-bold text-slate-900">{lowStock}</p>
          </div>

          {/* OUT OF STOCK */}
          <div className="flex-1 border-b sm:border-b-0 sm:border-r border-slate-200 p-4 flex flex-col items-center justify-center text-center">
            <p className="text-xs sm:text-sm text-slate-600">Out of Stock</p>
            <p className="text-2xl font-bold text-slate-900">{outOfStock}</p>
          </div>

          {/* HIGHEST AVAILABLE STOCK */}
          <div className="flex-1 p-4 flex flex-col items-center justify-center text-center">
            <p className="text-xs sm:text-sm text-slate-600">Highest Available Stock</p>
            <p className="font-bold text-slate-900 truncate">{mostStockProduct ? mostStockProduct.name : "-"}</p>
            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              {mostStockProduct ? `${mostStockProduct.quantityValue}${mostStockProduct.quantityUnit}` : ""}
            </span>
          </div>

        </div>
      </div>

      {/* ================= FILTER BAR (LIGHT SECTION) ================= */}
      <div className="border-t bg-[#EFF6FF] p-3 mb-0" >
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">

          {/* SEARCH */}

          <div className="relative w-full lg:flex-1 lg:min-w-[200px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </span>
            <input
              type="text"
              placeholder="Search…"
              className="pl-10 pr-3 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {/* FILTERS */}
          <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
            <div className="relative w-full sm:flex-1 lg:w-40 xl:w-44">
              <select
                className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                value={filterStock}
                onChange={(e) => setFilterStock(e.target.value)}
              >
                <option value="">All Stock</option>
                <option value="low">Low Stock</option>
                <option value="out">Out of Stock</option>
                <option value="most">Most Stock</option>
              </select>
            </div>

            <select
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:flex-1 lg:w-40 xl:w-44"
              value={filterFeatured}
              onChange={(e) => setFilterFeatured(e.target.value)}
            >
              <option value="">All Products</option>
              <option value="featured">Featured Only</option>
              <option value="non-featured">Non-Featured Only</option>
            </select>

            <select
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:flex-1 lg:w-40 xl:w-44"
              value={expiryFilter}
              onChange={(e) => setExpiryFilter(e.target.value)}
            >
              <option value="">All Expiry</option>
              <option value="expired">Expired</option>
              <option value="7days">Expire in 7 Days</option>
              <option value="30days">Expire in 30 Days</option>
              <option value="90days">Expire in 90 Days</option>
            </select>

            <select
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:flex-1 lg:w-40 xl:w-44"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="">Sort Qty</option>
              <option value="qty-low">Low → High</option>
              <option value="qty-high">High → Low</option>
            </select>
          </div>

        </div>
      </div>


      {/* ================= TABLE ================= */}
      <div className="border border-slate-200 rounded-lg bg-white shadow-sm mb-6">

        <div className="overflow-x-auto">
          <ThemedTable className="text-left border-collapse w-full">
            <thead>
              <tr className="bg-white text-gray-700 border-b border-slate-200">
                <th className="py-3 pl-3 pr-1 w-10 text-left text-slate-500 bg-white">#</th>
                <th className="py-3 px-1 w-12 text-left bg-white">Image</th>
                <th className="p-3 w-[25%] text-left bg-white">Product Details</th>
                <th className="p-3 w-[5%] text-center bg-white">GST</th>
                <th className="p-3 w-[16%] text-left bg-white">Pricing Tiers (Incl. GST)</th>
                <th className="p-3 w-[7%] text-center bg-white">Quantity</th>
                <th className="p-3 w-[7%] text-center bg-white">Stock</th>
                <th className="p-3 w-[10%] text-center bg-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-10 text-center">
                    <Loader size={32} />
                  </td>
                </tr>
              ) : (
                products
                  .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
                  .filter((p) => {
                    const total = getTotalStock(p.warehouses);
                    if (filterStock === "low") return total > 0 && total <= 10;
                    if (filterStock === "out") return total === 0;
                    if (filterStock === "most")
                      return mostStockProduct && p._id === mostStockProduct._id;
                    return true;
                  })
                  .filter((p) => {
                    if (filterFeatured === "featured") return p.featured === true;
                    if (filterFeatured === "non-featured") return p.featured !== true;
                    return true;
                  })
                  .filter((p) => {
                    if (!expiryFilter) return true;
                    if (!p.expiryDate) return false;
 
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
 
                    const expiry = new Date(p.expiryDate);
                    expiry.setHours(0, 0, 0, 0);
 
                    const diffDays = Math.ceil(
                      (expiry.getTime() - today.getTime()) /
                        (1000 * 60 * 60 * 24)
                    );
 
                    if (expiryFilter === "expired") {
                      return diffDays < 0;
                    }
                    if (expiryFilter === "7days") {
                      return diffDays >= 0 && diffDays <= 7;
                    }
                    if (expiryFilter === "30days") {
                      return diffDays >= 0 && diffDays <= 30;
                    }
                    if (expiryFilter === "90days") {
                      return diffDays >= 0 && diffDays <= 90;
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    if (sortBy === "qty-low")
                      return getTotalStock(a.warehouses) - getTotalStock(b.warehouses);
                    if (sortBy === "qty-high")
                      return getTotalStock(b.warehouses) - getTotalStock(a.warehouses);
                    return 0;
                  })
                  .map((p, idx) => (
                    <ProductRow
                      key={p._id}
                      p={p}
                      idx={idx}
                      user={user}
                      highlightId={activeHighlightId || highlightId}
                      setViewProductObj={handleViewDetails}
                      openEdit={openEdit}
                      setRestockProductObj={setRestockProductObj}
                      setShowRestockModal={setShowRestockModal}
                      setTransferProduct={setTransferProduct}
                      setShowTransferModal={setShowTransferModal}
                      setDeleteProductObj={setDeleteProductObj}
                      getInitials={getInitials}
                      getTotalStock={getTotalStock}
                    />
                  ))
              )}
            </tbody>
          </ThemedTable>
        </div>
        <div className="border-t border-slate-200 py-2 flex justify-center"></div>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 md:ml-64 mt-16 pb-0 flex flex-col">
          <div className="w-full flex-1 overflow-auto">

            <AddProducts
              productData={productData}
              setProductData={setProductData}
              brands={brands}
              categories={categories}
              units={units}
              setUnits={setUnits}
              locations={locations}
              setShowLocationModal={setShowLocationModal}
              thumbnailPreview={thumbnailPreview}
              imagesPreview={imagesPreview}
              handleThumbnail={handleThumbnail}
              handleImages={handleImages}
              removeImage={removeImage}
              onClose={() => setShowModal(false)}
              onSubmit={saveProduct}          // ✅ CONNECT SAVE
              isEdit={!!editingId}             // ✅ ADD vs EDIT
            />

            {showLocationModal && (
              <AddLocationModal
                show={showLocationModal}
                onClose={() => {
                  setShowLocationModal(false);
                  setNewLocationName("");
                  setNewAddress("");
                }}
                locationName={newLocationName}
                address={newAddress}
                setLocationName={setNewLocationName}
                setAddress={setNewAddress}
                onSave={handleAddLocationFromProduct}
              />
            )}

          </div>
        </div>
      )}
      {/* ================= TRANSFER STOCK MODAL ================= */}
      {showTransferModal && (
        <TransferStockModal
          show={showTransferModal}
          onClose={() => {
            setShowTransferModal(false);
            setTransferProduct(null);
          }}
          onTransfer={handleTransferStock}
          loading={loading}
          product={transferProduct}
          locations={locations}
        />
      )}

      {/* ================= RESTOCK MODAL ================= */}
      {showRestockModal && (
        <RestockModal
          show={showRestockModal}
          onClose={() => {
            setShowRestockModal(false);
            setRestockProductObj(null);
          }}
          onRestock={handleRestockStock}
          loading={loading}
          product={restockProductObj}
          locations={locations}
        />
      )}
      {/* ================= VIEW PRODUCT DETAILS MODAL ================= */}
      {viewProductObj && (
        <ProductDetailsModal
          product={viewProductObj}
          onClose={() => setViewProductObj(null)}
        />
      )}

      {/* ================= CONFIRM DELETE PRODUCT MODAL ================= */}
      <ConfirmDialog
        open={!!deleteProductObj}
        title="Delete Product"
        description={`Are you sure you want to delete ${deleteProductObj?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        onCancel={() => setDeleteProductObj(null)}
        onConfirm={() => deleteProduct(deleteProductObj._id)}
      />
    </div>
  );
};

const ProductRow = React.memo(({
  p,
  idx,
  user,
  highlightId,
  setViewProductObj,
  openEdit,
  setRestockProductObj,
  setShowRestockModal,
  setTransferProduct,
  setShowTransferModal,
  setDeleteProductObj,
  getInitials,
  getTotalStock
}) => {
  return (
    <tr 
      id={`product-row-${p._id}`}
      className={`transition duration-500 ${
        highlightId === p._id 
          ? "bg-amber-100 ring-2 ring-amber-400" 
          : "border-b border-slate-100 hover:bg-slate-50"
      }`}
    >
      <td className="py-3 pl-3 pr-1 w-10 font-semibold text-slate-500 text-left whitespace-nowrap">
        <div className="flex items-center justify-start gap-1">
          <span>{idx + 1}</span>
        </div>
      </td>
      <td className="py-3 px-1 w-12 text-left">
        {p.thumbnail ? (
          <img
            src={`${process.env.REACT_APP_IMAGE_URL}/${p.thumbnail}`}
            alt={p.name}
            className="w-10 h-10 rounded object-contain"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm">
            {getInitials(p.name)}
          </div>
        )}
      </td>
      <td className="p-3 w-[25%] font-medium">
        <div className="font-bold text-slate-800">{p.name}</div>
        <div className="text-[10px] mt-1 flex flex-wrap gap-1 font-normal">
          {p.brand?.name && (
            <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100">
              {p.brand.name}
            </span>
          )}
          {p.category?.name && (
            <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-100">
              {p.category.name}
            </span>
          )}
        </div>
        {p.servingDuration && (
          <div className="text-[10px] text-gray-500 mt-1 flex flex-wrap gap-1 font-normal">
            <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              Dur: {p.servingDuration}
            </span>
          </div>
        )}
      </td>
      {/* GST Rate */}
      <td className="p-3 w-[5%] text-center whitespace-nowrap text-xs text-slate-600 font-semibold">
        {p.taxRate || 0}%
      </td>

      {/* Pricing Tiers (Incl. GST) */}
      <td className="p-3 w-[16%] text-left text-[11px] leading-relaxed text-slate-700 whitespace-nowrap">
        <div className="flex flex-col gap-0.5">
          {p.mrp > 0 && (
            <div className="flex justify-between gap-4 max-w-[140px]">
              <span className="text-slate-400">MRP:</span>
              <span className="text-slate-700">₹{Number(p.mrp).toFixed(2)}</span>
            </div>
          )}
          {user?.role === "superAdmin" && (
            <div className="flex justify-between gap-4 max-w-[140px]">
              <span className="text-slate-500">Cost:</span>
              <span className="font-medium text-slate-600">₹{Number(p.purchasePriceWithGst || p.purchasePrice || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between gap-4 max-w-[140px]">
            <span className="text-slate-800 font-semibold">Retail:</span>
            <span className="font-bold text-slate-800">₹{Number(p.priceWithGst || p.price || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between gap-4 max-w-[140px]">
            <span className="text-orange-600 font-semibold">W-sale:</span>
            <span className="font-bold text-orange-600">₹{Number(p.wholesalePriceWithGst || p.wholesalePrice || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between gap-4 max-w-[140px]">
            <span className="text-blue-600 font-semibold">Gym:</span>
            <span className="font-bold text-blue-600">₹{Number(p.gymPriceWithGst || p.gymPrice || 0).toFixed(2)}</span>
          </div>
        </div>
      </td>
      <td className="p-3 w-[7%] text-center font-semibold">
        {p.quantityValue}
        <span className="text-gray-500">{p.quantityUnit}</span>
      </td>
      <td className="p-3 w-[7%] text-center font-semibold">
        {getTotalStock(p.warehouses)}
      </td>
     
      <td className="p-3 w-[10%] text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center gap-1.5">
          <button
            onClick={() => setViewProductObj(p)}
            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-emerald-600 bg-emerald-600 text-white hover:bg-white hover:text-emerald-600 transition"
            title="View Details"
          >
            <Eye size={14} />
          </button>
          <button
            onClick={() => openEdit(p)}
            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={() => {
              setRestockProductObj(p);
              setShowRestockModal(true);
            }}
            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition"
            title="Restock"
          >
            <Package size={14} />
          </button>
          <button
            onClick={() => {
              setTransferProduct(p);
              setShowTransferModal(true);
            }}
            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition"
            title="Transfer Stock"
          >
            <ArrowRightLeftIcon size={14} />
          </button>
          
          <button
            onClick={() => setDeleteProductObj(p)}
            className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.p === nextProps.p &&
    (prevProps.highlightId === prevProps.p._id) === (nextProps.highlightId === nextProps.p._id) &&
    prevProps.idx === nextProps.idx &&
    prevProps.user?.role === nextProps.user?.role
  );
});

export default Product;